/**
 * アップロード前にブラウザ側で画像を縮小・再エンコードする。
 *
 * 目的は通信量と待ち時間の削減であって、検証ではない。
 * クライアントを経由しない POST は普通に作れるので、
 * サーバー側の検証と再エンコードは別途必須（BannerUploadView 参照）。
 *
 * デコードできない形式（iPhone の HEIC など。Safari 以外は canvas で扱えない）は
 * 縮小せず原本をそのまま返す。サーバーが弾くか処理するかを決める。
 */

export interface PreparedImage {
  blob: Blob;
  filename: string;
  /** 縮小・再エンコードできたか。false なら原本をそのまま送っている */
  processed: boolean;
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// EXIF の回転を反映させる指定。これを付けないとスマホの縦写真が横倒しで保存される。
// この TypeScript の lib.dom は ImageOrientation に 'from-image' を持っていないが、
// ブラウザ側は対応済み（未知の値は無視されるだけなので、古い環境でも壊れない）。
const EXIF_ORIENTATION = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;

export interface PrepareOptions {
  /** 長辺の上限。cropTo を指定した場合は使われない */
  maxEdge?: number;
  quality?: number;
  /**
   * この寸法ぴったりに切り出す（cover 相当で縮小し中央を取る）。
   * サーバーの最終出力と同じ値を渡せば、プレビューと確定後の見え方が一致する。
   */
  cropTo?: { width: number; height: number };
  /**
   * 出力を JPEG に固定する。
   *
   * 既定は WebP 優先だが、**受け口によっては WebP を弾く**。
   * アイコン(AvatarUploadView)は JPEG / PNG しか許可していないので、
   * そこへ送るときは true にすること（バナーは WebP を受けるので既定のままでよい）。
   */
  forceJpeg?: boolean;
}

export async function prepareImageUpload(
  file: File,
  { maxEdge = 2000, quality = 0.85, cropTo, forceJpeg = false }: PrepareOptions = {}
): Promise<PreparedImage> {
  try {
    // imageOrientation を指定しないと、スマホ写真が EXIF の回転を無視して横倒しになる。
    // resizeWidth/Height は対応ブラウザではデコード時点で縮小してくれるので、
    // iOS Safari の canvas メモリ上限に引っかかりにくくなる（非対応なら無視される）。
    const bitmap = await createImageBitmap(file, EXIF_ORIENTATION);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return { blob: file, filename: file.name, processed: false };
    }

    if (cropTo) {
      // cover 相当。はみ出した側を中央基準で捨てる
      canvas.width = cropTo.width;
      canvas.height = cropTo.height;
      const scale = Math.max(cropTo.width / bitmap.width, cropTo.height / bitmap.height);
      const drawW = bitmap.width * scale;
      const drawH = bitmap.height * scale;
      ctx.drawImage(bitmap, (cropTo.width - drawW) / 2, (cropTo.height - drawH) / 2, drawW, drawH);
    } else {
      const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    }
    bitmap.close();

    // WebP が拒否されると toBlob は PNG を返す（= かえって重い）ので、
    // 実際に webp が返ったときだけ採用する。
    if (!forceJpeg) {
      const webp = await canvasToBlob(canvas, 'image/webp', quality);
      if (webp && webp.type === 'image/webp') {
        return { blob: webp, filename: 'upload.webp', processed: true };
      }
    }
    const jpeg = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (jpeg) {
      return { blob: jpeg, filename: 'upload.jpg', processed: true };
    }
    return { blob: file, filename: file.name, processed: false };
  } catch {
    // HEIC など canvas でデコードできない形式
    return { blob: file, filename: file.name, processed: false };
  }
}
